import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxContent, Container, Content } from './styles';
import { CoreApiContext } from '../../../contexts/coreApiContext';
import { getLanguage, getAccountType } from '../../../services/auth';
import { Keys } from '../../../constants';
import { logError } from '../../../utils/logError';
import { getFromLocalStorage, removeFromLocalStorage } from '../../../utils/localStorage';
import { sendEventLog } from '../../../services/eventLog';
import { setDataLayer } from '../../../services/gtm';
import { transcodeLanguage } from '../../../utils/language';
import Header from '../../../components/Header';
import Loading from '../../../components/atoms/Loading';
import Notification from '../../../components/Notification';
import OperationCard from '../components/OperationCard';
import ContactCard from '../components/ContactCard';
import OnboardingActions from '../components/OnboardingActions';
import {
  updateAccountOnboardingClassification,
  updateAccountOnboardingStep,
  updateAccountOnboardingWhatsapp,
  updateAccountStartProfiles,
} from '../../../services/accountOnboarding';
import {
  buildEnhancedConversionHashes,
  toGoogleAdsUserData,
} from '../../../utils/enhancedConversionUserData';
import { countryCode, defaultCountryCode } from '../../../config/i18n';
import { getCountryCode } from '../../../utils/countryCodes';
import { isPhoneInvalid, phoneValidation } from '../../../utils/validations';
import {
  ONBOARDING_STEPS,
  UI_SCREENS,
  getScreenHistoryForUi,
  getUiScreenForStep,
  isOnboardingCompleted,
  resolveFranchiseScopeAnswer,
  resolveOperationAnswer,
  resolvePostOnboardingRedirectUrl,
  resolveScaleAnswer,
} from '../../../constants/onboardingOperation';

const STEP_OPERATION = 0;
const STEP_CONTACT = 1;

const CLASSIFICATION_SCREENS = [
  UI_SCREENS.OPERATION,
  UI_SCREENS.AGENCY_SCALE,
  UI_SCREENS.FRANCHISE_SCOPE,
  UI_SCREENS.FRANCHISE_SCALE,
  UI_SCREENS.OTHER_SCALE,
];

export default function Onboarding() {
  const { currentAccount } = useContext(CoreApiContext);
  const [startProfilesCount, setStartProfilesCount] = useState();
  const [typeDescription, setTypeDescription] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [canShowProfilesMenu, setCanShowProfilesMenu] = useState(false);
  const [notification, setNotification] = useState({});
  const [activeStep, setActiveStep] = useState(STEP_OPERATION);
  const [uiScreen, setUiScreen] = useState(UI_SCREENS.OPERATION);
  const [selectedKey, setSelectedKey] = useState(null);
  const [screenHistory, setScreenHistory] = useState([UI_SCREENS.OPERATION]);
  const [isFlagResolved, setIsFlagResolved] = useState(false);
  const [phone, setPhone] = useState(() => ({
    countryCode: countryCode[getLanguage()] || defaultCountryCode,
    number: '',
    phoneValidation: {},
  }));

  const { t } = useTranslation();
  const couponCode = getFromLocalStorage(Keys.COUPON_CODE) || '';
  const language = transcodeLanguage(getLanguage());
  const accountType = getAccountType() || 'email';
  const registerType = accountType === 'email' ? 'metodo_formulario' : 'metodo_facebook';
  const userPlanType = currentAccount?.quotas?.available ? 'subscriber' : 'trial';

  const contactStepRef = useRef(false);
  const resumeAppliedRef = useRef(false);

  const showNotification = (message) => {
    setNotification({
      key: new Date().getTime(),
      message,
    });
  };

  const sendEventGtm = async ({ element, elementText, numberOfSelectedProfiles, userAction }) => {
    const hashes = await buildEnhancedConversionHashes({
      email: currentAccount?.email,
      userPhoneSHA256: currentAccount?.userPhoneSHA256,
    });

    setDataLayer({
      event: 'siteFireEvt.accountsCreationFlow.conversionEvent',
      eventProperty: {
        numberOfSelectedProfiles,
        element,
        elementText,
        userAction,
        primarySource: 'app',
        codeSource: 'front-->gtm-->endpoint',
        analiticalContext: 'comportamental',
        primaryContext: 'accounts',
        elementOriginalText: 'nao_aplicavel',
        appContext: 'cadastrar',
        appAction: 'finalizar_cadastro_step_perfis',
        socialNetwork: 'nao_aplicavel',
        postChannel: 'nao_aplicavel',
        numberOfDatesToPost: 0,
        totalPostScheduled: 0,
        appDetails: `${registerType} cadastro_realizado_novo_usuario`,
        siteLanguage: language,
        registerMethod: registerType,
        siteEventDetails: `cadastrar.finalizar_cadastro_step_perfis.${registerType}.cadastro_realizado_novo_usuario`,
      },
      userProperty: {
        userCostumerStatus: userPlanType,
        ...hashes,
      },
      user_data: toGoogleAdsUserData(hashes),
      transactionProperty: {
        transactionId: currentAccount?.id ? String(currentAccount.id) : '',
        accountNumber: currentAccount?.id,
      },
    });
  };

  const handleChatTrackingEvent = async ({ numberOfSelectedProfiles, email }) => {
    try {
      const hashes = await buildEnhancedConversionHashes({
        email,
        userPhoneSHA256: currentAccount?.userPhoneSHA256,
      });

      setDataLayer({
        event: 'siteFireEvt.accountsCreationFlow.conversionEvent',
        eventProperty: {
          codeSource: 'native-->dataLayer-->gtm-->endpoint',
          primarySource: 'chat_webapp',
          primaryContext: 'accounts',
          analiticalContext: 'comportamental',
          userAction: 'visitou',
          element: 'pagina',
          elementText: 'nao_aplicavel',
          elementOriginalText: 'nao_aplicavel',
          siteEventDetails:
            'cadastrar.finalizar_cadastro.metodo_formulario.cadastro_realizado_novo_usuario',
          socialNetwork: 'nao_aplicavel',
          postChannel: 'nao_aplicavel',
          appContext: 'cadastrar',
          appAction: 'finalizar_cadastro',
          appDetails: 'metodo_formulario.cadastro_realizado_novo_usuario',
          siteLanguage: language,
          registerMethod: 'from_chat-accounts.mlabs.io',
          numberOfSelectedProfiles,
          numberOfDatesToPost: 0,
          totalPostScheduled: 0,
          isFirstChatConnectedNetwork: false,
        },
        userProperty: {
          ...hashes,
          userCostumerStatus: userPlanType,
          ecommerce: {
            // eslint-disable-next-line camelcase
            social_media_name: '',
          },
        },
        user_data: toGoogleAdsUserData(hashes),
        transactionProperty: {
          transactionId: currentAccount?.id ? String(currentAccount.id) : '',
          accountNumber: currentAccount?.id,
        },
      });
    } catch (error) {
      logError('Error sending gtm tracking event');
    }
  };

  function accessRightStepProfiles(account) {
    const accessRights = account.access_rights;
    const access = 'sign_up_step_profiles';

    return access in accessRights ? accessRights[access] : true;
  }

  const goToScreen = (nextScreen, { pushHistory = true } = {}) => {
    setSelectedKey(null);
    setUiScreen(nextScreen);

    if (nextScreen === UI_SCREENS.CONTACT) {
      setActiveStep(STEP_CONTACT);
      return;
    }

    setActiveStep(STEP_OPERATION);
    if (pushHistory) {
      setScreenHistory((prev) => [...prev, nextScreen]);
    }
  };

  const persistClassification = async ({
    typeDescription,
    startProfilesCount,
    onboardingStep,
    includeExtras = false,
  }) => {
    const payload = {
      typeDescription,
      startProfilesCount,
      onboardingStep,
    };

    if (includeExtras) {
      payload.language = language;
      payload.couponCode = couponCode;
    }

    await updateAccountOnboardingClassification(currentAccount.id, payload);
  };

  const redirectAfterOnboarding = () => {
    const resolvedType = typeDescription || currentAccount?.type_descrition;
    const resolvedProfiles =
      typeof startProfilesCount === 'number'
        ? startProfilesCount
        : currentAccount?.start_profiles_count;

    window.location.assign(
      resolvePostOnboardingRedirectUrl({
        typeDescription: resolvedType,
        startProfilesCount: resolvedProfiles,
      }),
    );
  };

  const enterContactStep = async (nextStartProfilesCount) => {
    setIsLoading(true);
    goToScreen(UI_SCREENS.CONTACT);

    if (contactStepRef.current) {
      setIsLoading(false);
      return;
    }

    contactStepRef.current = true;

    try {
      if (
        !currentAccount.onboarding_step ||
        currentAccount.onboarding_step < ONBOARDING_STEPS.CONTACT
      ) {
        await updateAccountOnboardingStep(currentAccount.id, ONBOARDING_STEPS.CONTACT);
      }
    } catch (error) {
      // Classificação (step 3) já persistida: permanece no Contact e notifica.
      // contactStepRef fica false para permitir retentar o PATCH do step 4.
      contactStepRef.current = false;
      showNotification(error?.response?.data?.message || t('errors.connection-failure'));
      setIsLoading(false);
      return;
    }

    if (typeof nextStartProfilesCount === 'number') {
      setStartProfilesCount(nextStartProfilesCount);
    }

    setIsLoading(false);
  };

  const trackClassificationComplete = async (profilesCount) => {
    removeFromLocalStorage(Keys.ACCOUNT_CREATOR);
    removeFromLocalStorage(Keys.COUPON_CODE);

    try {
      sendEventLog('uso_onboarding_continuar');

      if (process.env.REACT_APP_PRODUCT_KEY === 'chat') {
        await handleChatTrackingEvent({
          numberOfSelectedProfiles: profilesCount,
          email: currentAccount?.email,
        });
      } else {
        await sendEventGtm({
          numberOfSelectedProfiles: profilesCount,
          element: 'botao',
          elementText: 'Iniciar período de teste',
          userAction: 'clicou',
        });
      }

      try {
        await sendEventLog('signup_click_start-trial-period-button', {
          origin: currentAccount.origin_key,
        });
      } catch {
        logError(t('errors.send-event-log'));
      }
    } catch (error) {
      logError('Error tracking classification complete');
    }
  };

  const applyAnswer = async (answer) => {
    if (!answer) {
      return;
    }

    setIsLoading(true);

    try {
      const isComplete = answer.nextScreen === UI_SCREENS.CONTACT;

      await persistClassification({
        typeDescription: answer.typeDescription,
        startProfilesCount: answer.startProfilesCount,
        onboardingStep: answer.onboardingStep,
        includeExtras: isComplete && typeof answer.startProfilesCount === 'number',
      });

      if (answer.typeDescription) {
        setTypeDescription(answer.typeDescription);
      }

      if (typeof answer.startProfilesCount === 'number') {
        setStartProfilesCount(answer.startProfilesCount);
      }

      if (isComplete) {
        await trackClassificationComplete(answer.startProfilesCount);
        await enterContactStep(answer.startProfilesCount);
        return;
      }

      goToScreen(answer.nextScreen);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      showNotification(error?.response?.data?.message || t('errors.operation-save-failure'));
    }
  };

  const handleCardSelect = (key) => {
    setSelectedKey(key);
  };

  const handleContinueClassification = async () => {
    if (!selectedKey) {
      return;
    }

    if (uiScreen === UI_SCREENS.OPERATION) {
      sendEventLog('uso_onboarding_operacao');
      await applyAnswer(resolveOperationAnswer(selectedKey));
      return;
    }

    if (uiScreen === UI_SCREENS.FRANCHISE_SCOPE) {
      sendEventLog('uso_onboarding_franquia_escopo');
      await applyAnswer(resolveFranchiseScopeAnswer(selectedKey));
      return;
    }

    sendEventLog('uso_onboarding_faixa');
    await applyAnswer(resolveScaleAnswer(uiScreen, selectedKey));
  };

  const handleFinish = async () => {
    setIsLoading(true);

    try {
      if (phone.number) {
        if (isPhoneInvalid(phone.number, phone.countryCode)) {
          setPhone((currentPhone) => ({
            ...currentPhone,
            phoneValidation: phoneValidation(currentPhone.number, currentPhone.countryCode),
          }));
          setIsLoading(false);
          return;
        }

        await updateAccountOnboardingWhatsapp(currentAccount.id, {
          phoneCode: getCountryCode(phone.countryCode),
          phoneNumber: phone.number,
        });
      }

      await updateAccountOnboardingStep(currentAccount.id, ONBOARDING_STEPS.COMPLETED);

      setDataLayer({
        event: 'onboarding_start_trial',
        eventProperty: {
          appAction: 'iniciar_teste_gratis',
          appContext: 'onboarding_contato',
        },
      });

      sendEventLog('uso_onboarding_IniciarTeste');
      redirectAfterOnboarding();
    } catch (error) {
      setIsLoading(false);
      showNotification(error?.response?.data?.message || t('errors.connection-failure'));
    }
  };

  const handleSkipContact = async () => {
    setIsLoading(true);

    try {
      await updateAccountOnboardingStep(currentAccount.id, ONBOARDING_STEPS.COMPLETED);
    } catch (error) {
      setIsLoading(false);
      showNotification(error?.response?.data?.message || t('errors.connection-failure'));
      return;
    }

    setDataLayer({
      event: 'onboarding_skip_contact',
      eventProperty: {
        appAction: 'pular_contato',
        appContext: 'onboarding_contato',
      },
    });

    sendEventLog('uso_onboarding_pular_contato');
    redirectAfterOnboarding();
  };

  const handleContinue = async () => {
    if (activeStep === STEP_CONTACT) {
      await handleFinish();
    }
  };

  const handleRedirectAccountPartner = async () => {
    await updateAccountStartProfiles(currentAccount.id, {
      startProfilesCount: 0,
      language,
      couponCode: '',
    });

    window.location.assign(process.env.REACT_APP_FULL_URL);
  };

  const handleBackButton = () => {
    sendEventLog('uso_onboarding_voltar');

    if (activeStep === STEP_CONTACT) {
      contactStepRef.current = false;
      goToScreen(UI_SCREENS.OPERATION, { pushHistory: false });
      setScreenHistory([UI_SCREENS.OPERATION]);
      return;
    }

    if (screenHistory.length <= 1) {
      return;
    }

    const nextHistory = screenHistory.slice(0, -1);
    const previousScreen = nextHistory[nextHistory.length - 1];
    setScreenHistory(nextHistory);
    setSelectedKey(null);
    setUiScreen(previousScreen);
    setActiveStep(STEP_OPERATION);
  };

  const handleGtmTrackingEvent = async () => {
    if (process.env.REACT_APP_PRODUCT_KEY === 'chat') {
      await handleChatTrackingEvent({ numberOfSelectedProfiles: 0, email: currentAccount?.email });
      return;
    }

    await sendEventGtm({
      numberOfSelectedProfiles: 0,
      userAction: 'visitou',
      element: 'pagina',
      elementText: 'nao_aplicavel',
    });
  };

  useEffect(() => {
    setIsFlagResolved(true);
  }, []);

  useEffect(() => {
    if (
      isFlagResolved &&
      currentAccount?.id &&
      isOnboardingCompleted(currentAccount.onboarding_step)
    ) {
      window.location.assign(process.env.REACT_APP_FULL_URL);
    }
  }, [isFlagResolved, currentAccount]);

  useEffect(() => {
    if (!currentAccount?.id) {
      return;
    }

    handleGtmTrackingEvent();

    if (!accessRightStepProfiles(currentAccount)) {
      handleRedirectAccountPartner();
      return;
    }

    setCanShowProfilesMenu(true);

    if (resumeAppliedRef.current) {
      return;
    }

    const persistedStep = currentAccount.onboarding_step;
    if (persistedStep && persistedStep > ONBOARDING_STEPS.NOT_STARTED) {
      resumeAppliedRef.current = true;
      const resumedScreen = getUiScreenForStep(persistedStep, currentAccount.type_descrition);
      setUiScreen(resumedScreen);
      setScreenHistory(getScreenHistoryForUi(resumedScreen));
      setActiveStep(resumedScreen === UI_SCREENS.CONTACT ? STEP_CONTACT : STEP_OPERATION);

      if (currentAccount.start_profiles_count) {
        setStartProfilesCount(currentAccount.start_profiles_count);
      }

      if (currentAccount.type_descrition) {
        setTypeDescription(currentAccount.type_descrition);
      }

      // Step ≥ 3 e < 4: usuário já classificado — garante Contact e grava step 4.
      if (
        persistedStep >= ONBOARDING_STEPS.OPERATION_COMPLETE &&
        persistedStep < ONBOARDING_STEPS.CONTACT
      ) {
        enterContactStep(currentAccount.start_profiles_count);
      }
    }
  }, [currentAccount]);

  const isClassificationScreen = CLASSIFICATION_SCREENS.includes(uiScreen);
  const canGoBackClassification = isClassificationScreen && screenHistory.length > 1;

  return (
    <Container>
      {(!canShowProfilesMenu || isLoading || !isFlagResolved) && <Loading opacity />}
      {canShowProfilesMenu && isFlagResolved && (
        <>
          <Header showLogo />
          <Content>
            <Box $maxWidth={isClassificationScreen ? '680px' : '560px'}>
              <BoxContent>
                {isClassificationScreen ? (
                  <>
                    <OperationCard
                      screenKey={uiScreen}
                      selectedKey={selectedKey}
                      onSelect={handleCardSelect}
                    />
                    <OnboardingActions
                      onBack={canGoBackClassification ? handleBackButton : undefined}
                      onContinue={handleContinueClassification}
                      continueDisabled={!selectedKey}
                      loading={isLoading}
                      backLabel={t('button.back')}
                      continueLabel={t('settings.steps.continue')}
                      showContinueArrow
                    />
                  </>
                ) : (
                  <>
                    <ContactCard phone={phone} onPhoneChange={setPhone} />
                    <OnboardingActions
                      onBack={handleBackButton}
                      onSkip={handleSkipContact}
                      onContinue={handleContinue}
                      loading={isLoading}
                      backLabel={t('button.back')}
                      skipLabel={t('button.skip')}
                      continueLabel={t('settings.steps.contact.startTrial')}
                      showContinueArrow
                    />
                  </>
                )}
              </BoxContent>
            </Box>
          </Content>
          {!!notification?.key && (
            <Notification index={notification.key} message={notification.message} />
          )}
        </>
      )}
    </Container>
  );
}
